import { useState, type FormEvent } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import type { MultiFactorResolver } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { isMfaRequiredError, mfaResolver, completeMfaSignIn, mfaErrorText } from '../lib/mfa';
import { hostBrand } from '../host-brand';
import { Wordmark } from '../brand';
import { useTheme } from '../theme';

/**
 * Email + password sign-in.
 *
 * There is no self-service registration: accounts are created by a manager
 * through Invite, which emails a one-time sign-in link. Anyone who should see
 * this dashboard was put here by someone who meant it.
 *
 * RESET IS SENT BY US, NOT BY FIREBASE. The callable mints the action link with
 * the Admin SDK and delivers it through Resend, so the message comes from the
 * same verified domain as the alerts. A password email arriving from a Google
 * address nobody recognises is indistinguishable from a phishing attempt.
 */
/**
 * The mark above the sign-in fields.
 *
 * Reads the resolved host branding SYNCHRONOUSLY - resolveHostBrand() ran and
 * was awaited before createRoot, so by the time this renders the answer is
 * already sitting in memory. No loading state, no flicker from our mark to
 * theirs, which on a login page reads as the wrong site having loaded first.
 */
function SignInBrand() {
  const brand = hostBrand();
  const [theme] = useTheme();
  const logo = theme === 'dark'
    ? (brand.logoDark || brand.logoLight)
    : (brand.logoLight || brand.logoDark);

  // Not a mapped host: the product's own lockup, at full size. It already
  // carries the name, so there is nothing to put beside it.
  if (!logo) return <div className="signin-brand"><Wordmark width={190} /></div>;

  /*
   * THE TENANT'S MARK, WITH OURS SMALL BENEATH IT.
   *
   * No name text under either. A logo that needs its own company's name spelled
   * out beside it is not doing its job, and the tenant title was a THIRD thing
   * competing with two marks on the quietest screen in the product. The
   * hierarchy does the work instead: their mark at full size because it is
   * their sign-in page, ours at two-thirds because they should know whose
   * software it is without being told twice.
   *
   * Same arrangement Conduit uses on a tenant subdomain - their logo with the
   * product wordmark underneath.
   */
  return (
    <div className="signin-brand">
      <img className="signin-tenant-logo" src={logo} alt={brand.title ?? ''} />
      <Wordmark width={126} />
    </div>
  );
}
export function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Set when Firebase interrupts sign-in for a second factor. */
  const [pending, setPending] = useState<MultiFactorResolver | null>(null);
  const [code, setCode] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      // Not a failure: the password was RIGHT and Firebase wants the second
      // factor. No session exists yet - the resolver carries the pending
      // sign-in and it completes only once the code checks out.
      if (isMfaRequiredError(err)) {
        setPending(mfaResolver(err));
        setBusy(false);
        return;
      }
      // Never distinguish "no such user" from "wrong password" - that turns the
      // form into an account enumeration oracle.
      setError(
        err instanceof Error && err.message.includes('too-many-requests')
          ? 'Too many attempts. Wait a few minutes and try again.'
          : 'That email and password combination was not accepted.'
      );
      setBusy(false);
    }
  }

  async function reset() {
    const addr = email.trim();
    if (!addr) {
      setError('Enter your email address first, then choose Forgot password.');
      return;
    }
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      // The host goes with it so the reset link comes back on the address they
// are actually using, not the Firebase default.
await httpsCallable(functions, 'requestPasswordReset')({
  email: addr, host: window.location.hostname,
});
    } catch {
      // Deliberately swallowed. The server answers identically for a known and
      // an unknown address; surfacing a client-side failure here would leak the
      // difference the server just took care to hide.
    }
    // The SAME message either way, for the same reason.
    setNote(`If an account exists for ${addr}, a reset link is on its way. It expires within the hour.`);
    setBusy(false);
  }

  return (
    <div className="signin-wrap">
      <form
        className="signin-card"
        onSubmit={pending
          ? (e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              completeMfaSignIn(pending, code).catch((err) => {
                setError(mfaErrorText(err));
                setBusy(false);
              });
            }
          : submit}
      >
        {/*
          * WHOSE SIGN-IN PAGE IS THIS?
          *
          * Hardcoded "Backup Monitor / Vital Technology Group" until now, which
          * was fine while VTG was the only tenant and wrong the moment anybody
          * else reached it - including every customer arriving at their own
          * domain, who would have been shown another company's name at the
          * exact moment they were deciding whether to trust the page.
          *
          * host-brand.ts has already resolved this from the hostname, before
          * React rendered, with no session. If this host belongs to a tenant we
          * show THEIR logo and name; otherwise the product's own mark. Either
          * way it is decided by the address that was typed, which is the only
          * thing we know about the visitor at this point.
          */}
        <SignInBrand />

        {pending ? (
          <>
            <p className="panel-note" style={{ marginTop: 0 }}>
              Enter the six-digit code from your authenticator app.
            </p>
            <label className="field">
              <span className="field-label">Code</span>
              <input
                className="field-input mono" inputMode="numeric" maxLength={6} autoFocus
                placeholder="000000" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </label>
            {error && <div className="signin-error">{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6}>
              {busy ? 'Checking…' : 'Verify'}
            </button>
            <button
              type="button" className="btn-link signin-forgot"
              onClick={() => { setPending(null); setCode(''); setError(null); }}
            >
              Start over
            </button>
            <div className="signin-version">v{__APP_VERSION__}</div>
          </>
        ) : (
        <>
        <label className="field">
          <span className="field-label">Email</span>
          <input
            className="field-input"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">Password</span>
          <input
            className="field-input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <div className="signin-error">{error}</div>}
        {note && <div className="signin-note">{note}</div>}

        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {/* type="button" matters: inside a form, a bare button submits it, which
            would fire a sign-in attempt with a blank password every time. */}
        <button type="button" className="btn-link signin-forgot" disabled={busy} onClick={reset}>
          Forgot password?
        </button>

        <div className="signin-version">v{__APP_VERSION__}</div>
        </>
        )}
      </form>
    </div>
  );
}
